import React from "react";
import Image from "next/image";

interface CaseStudy {
	label: string;
	title: string;
	description: string;
}

interface Testimonial {
	quote: string;
	title: string;
	company: string;
}

interface Logo {
	src: string;
	alt: string;
}

interface SocialProofSectionProps {
	caseStudy?: CaseStudy;
	testimonial?: Testimonial;
	showCaseStudy?: boolean;
	sectionTitle?: string;
	additionalText?: string;
	logos?: Logo[];
	logosLabel?: string;
}

const SocialProofSection = ({
	caseStudy = {
		label: "Case Study Highlight:",
		title: "A national brand needed to redesign and rebuild a complex customer experience — fast.",
		description:
			"Crafted integrated with their product team, re-architected the UI, increased performance, and delivered a launch-ready product far ahead of internal projections.",
	},
	testimonial = {
		quote:
			"Crafted operates like a world-class internal team. Fast, thoughtful, reliable — and every deliverable just feels right.",
		title: "Director of Digital Experience",
		company: "USA Football",
	},
	showCaseStudy = true,
	sectionTitle = "",
	additionalText = "",
	logos = [
		{ src: "/images/republic.png", alt: "Republic Airways" },
		{ src: "/images/CrewCarwash.png", alt: "Crew Carwash" },
		{ src: "/images/USA.png", alt: "USA Football" },
		{ src: "/images/MISO.png", alt: "MISO Energy" },
	],
	logosLabel = "Trusted by:",
}: SocialProofSectionProps) => {
	return (
		<div className="bg-black text-white py-[clamp(40px,6.25vw,80px)] md:py-36 px-2 xl:px-0">
			<div className="socialProofSection container mx-auto px-4 max-w-6xl">
				<h2
					className={`text-3xl md:text-4xl font-bold mb-12 md:mb-24 ${
						showCaseStudy ? "text-center" : "text-left"
					}`}
				>
					{sectionTitle}
				</h2>

				<div
					className={`relative overflow-hidden mb-12 md:mb-24 items-center ${
						showCaseStudy ? "bg-white/10 p-8 md:p-10 border border-blue rounded-[10px]" : ""
					}`}
				>
					{/* <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-600 to-purple-600 opacity-50"></div> */}

					<div
						className={`grid grid-cols-1 md:grid-cols-[repeat(16,minmax(0,1fr))] gap-10 items-center`}
					>
						{showCaseStudy && (
							<div className="col-span-1 md:col-span-10">
								<p className="text-blue font-semibold mb-4 text-sm tracking-wider">
									{caseStudy.label}
								</p>
								<h3 className="text-2xl font-bold mb-6">{caseStudy.title}</h3>
								<p className="text-white">{caseStudy.description}</p>
							</div>
						)}

						<div
							className={`testimonialCard bg-white/10 p-6 rounded-xl italic text-gray-300 relative flex justify-start col-span-1 ${
								showCaseStudy ? "md:col-span-6" : "md:col-[14_/_span_14] md:col-start-4"
							}`}
						>
							<div className="relative w-[2px] rounded-[10px] mr-6 bg-blue"></div>
							<div>
								<p
									className={`mb-4 relative z-10 text-white ${
										showCaseStudy ? "text-base" : "text-2xl"
									}`}
								>
									&ldquo;{testimonial.quote}&rdquo;
								</p>
								<div className="not-italic">
									<p
										className={`text-white text-xs leading-relaxed ${
											showCaseStudy ? "text-xs" : "text-base"
										}`}
									>
										<span className="font-semibold">{testimonial.title}</span>
										<br />
										{testimonial.company}
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>

				{logos.length > 0 && (
					<div className="flex flex-col items-start gap-8">
						<div className="text-[clamp(18px,1.88vw,24px)] font-semibold text-white min-w-max">
							{logosLabel}
						</div>
						<div className="flex flex-col md:flex-row flex-nowrap justify-center items-center gap-[clamp(25px,3.13vw,40px)] w-full">
							{logos.map((logo, index) => (
								<div
									key={index}
									className="relative flex-auto md:flex-1 w-full max-w-[220px] md:w-auto md:max-w-none h-16 md:h-20 max-h-[80px]"
								>
									<Image
										src={logo.src}
										alt={logo.alt}
										layout="fill"
										objectFit="contain"
										className="transition-all duration-300"
									/>
								</div>
							))}
						</div>
						{additionalText && (
							<div className="text-lg md:text-2xl text-white font-semibold text-center md:text-right hidden md:block w-full">
								{additionalText}
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default SocialProofSection;
